/**
 * Channels Submit Policy (capability-aware)
 *
 * Handles channel creation flow by branching on the selected connector's
 * declared `capabilities` instead of an id-based switch:
 *
 *   - `oauth`   → redirect to the connector's authorization URL
 *   - `api-key` → call ProvisionSystemIntegration with the form
 *                 credentials
 *   - neither   → server-configured connector; setup-submit is a no-op
 *                 contract violation (the modal should never render an
 *                 actionable button for this branch)
 *
 * The capability resolution is centralized in `capabilityGates.ts` so
 * the modal (panel selection) and the policy (submit branch) stay in
 * sync.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';
import { ConnectorEntry } from '../../../shared/state/state';
import {
  resolveSetupPanelKind,
  canSubmitOAuth,
  canSubmitApiKey
} from '../../../shared/state/capabilityGates';

// Channels whose OAuth URL is a template that embeds the user-supplied
// store URL (TiendaNube, Shopify, Fenicio). The store URL slot is the
// connector's contract, not a policy responsibility — but we still need
// to know which connectors carry it. Capability-driven detection: any
// connector whose declared `oauth` URL contains the `{store_url}`
// placeholder needs the store-URL field collected before redirect.
const needsStoreUrlInAuthUrl = ( authUrl: string ): boolean =>
  authUrl.includes( '{store_url}' );

export const createChannelsSubmitPolicy = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channels-submit',

  shouldReact: ( prev, next ) => {
    return (
      prev.addChannelModal.step !== 'connecting' &&
      next.addChannelModal.step === 'connecting' &&
      next.loading.submitting
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const { addChannelModal } = next;
    const { selectedType, formData } = addChannelModal;

    const resetSubmitting = () => {
      ctx.dispatch(
        { type: 'integrations/channels/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'integrations/channels/addModal/setStep', payload: { step: 'enterDetails' }},
        { source: 'api' }
      );
    };

    if ( !selectedType ) {
      console.error( '[channels] No connector selected' );
      resetSubmitting();
      return;
    }

    const connectors = getConnectors();
    const connector = connectors[selectedType];
    const panelKind = resolveSetupPanelKind( connector );

    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- console.error operator log + submit-loading reset only, no user surface (TV1-2010 waiver, not a rethrow).
    try {
      if ( panelKind === 'oauth' ) {
        // Capability-gate: defensive check. The modal should not have
        // routed a non-oauth connector through this branch, but if it
        // did we fail loudly rather than silently mis-redirecting.
        if ( !canSubmitOAuth( connector )) {
          throw new Error(
            `Connector ${selectedType} does not declare the oauth capability`
          );
        }

        // @ts-ignore - authUrl property missing in type definition during the rename window
        const baseUrl: string = connector?.authUrl || '';
        let authUrl = baseUrl;

        if ( needsStoreUrlInAuthUrl( baseUrl ) && formData.storeUrl ) {
          const storeUrl = formData.storeUrl.replace( /\/$/, '' );
          authUrl = baseUrl.replace( '{store_url}', storeUrl );
        }

        if ( !authUrl ) {
          throw new Error( `No auth URL configured for ${selectedType}` );
        }

        window.location.href = authUrl;
        return;
      }

      if ( panelKind === 'apiKey' ) {
        if ( !canSubmitApiKey( connector )) {
          throw new Error(
            `Connector ${selectedType} does not declare the api-key capability`
          );
        }

        await backend.commands.ProvisionSystemIntegration({
          id: uuidv7(),
          body: {
            connectorId: selectedType,
            credentials: { ...formData }
          } as any
        });

        ctx.dispatch(
          { type: 'integrations/channels/addModal/close', payload: {}},
          { source: 'api' }
        );
        ctx.dispatch(
          { type: 'integrations/channels/loading/setSubmitting', payload: { isLoading: false }},
          { source: 'api' }
        );
        return;
      }

      // panelKind === 'serverConfigured' — no setup action exists.
      console.warn(
        `[channels] Submit attempted on server-configured connector ${selectedType}; ignoring.`
      );
      resetSubmitting();
    } catch ( error ) {
      console.error( '[channels] Submit failed:', error );
      resetSubmitting();
    }
  }
});
